import { LoadingOutlined } from "@ant-design/icons";
import { Empty, notification, Pagination, Select, Tooltip } from "antd";
import classNames from "classnames";
import AccessControl from "components/common/AccessControl/accessControl";
import FilterPopover from "components/common/FilterPopover/component";
import SVG from "components/common/SVG";
import { allUserRoles, clientRoles, ENTITY_TYPE, roleLabels, userRoleType } from "constants/phase";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import DeleteModal from "../../../../components/common/DeleteModal";
import AddUserModal from "../../../../components/common/Modals/AddUserModal";
import AssignUserModal from "../../../../components/common/Modals/AssignUserModal";
import { convertIsoDateToStandardFormat } from "../../../../utils/date";
import styles from "./style.module.scss";

const pageSize = 5;

const roleSelectOptions = (role, isAdmin = false, superAdmin = false) => {
  if (Object.values(clientRoles).includes(role) && superAdmin) {
    return Object.values(clientRoles).map((role) => ({ label: roleLabels[role], value: role }));
  } else if (Object.values(clientRoles).includes(role)) {
    return Object.values(clientRoles)
      .map((role) => ({ label: roleLabels[role], value: role }))
      .filter((role) => isAdmin && role.value !== allUserRoles.admin);
  } else if (role === userRoleType.generalOps || role === userRoleType.privilegeOps) {
    return [{ value: role, label: roleLabels[role] }];
  }

  return [];
};

const Rows = ({ users, updateUserRole, updateUserHandler, setShowRemoveUserModal, isAdmin, isSuperAdmin }) => {
  return users.map((user) => {
    return (
      <tr key={user.userId}>
        <td>{user?.name}</td>
        <td>{user?.createdAt ? convertIsoDateToStandardFormat(user?.createdAt) : ""}</td>
        <td>{user?.email}</td>
        <td>
          <Select
            showSearch={false}
            allowClear={false}
            disabled={
              user?.role === userRoleType.privilegeOps ||
              user?.role === userRoleType.generalOps ||
              (isAdmin && user?.role === allUserRoles.admin)
            }
            optionFilterProp="children"
            style={{ maxWidth: "140px" }}
            className="custom-ant-select-v2"
            value={user?.role}
            onChange={(role) => updateUserRole(user.userId, role)}
            options={roleSelectOptions(user?.role, isAdmin, isSuperAdmin)}
          />
        </td>
        <td>
          <div className={styles.actionContainer}>
            <AccessControl code="update_user" page="organization-profile">
              <Tooltip placement="top" title={"Update User Role"} arrow>
                <button
                  className={classNames(styles.updateCta, {
                    [styles.disabledCta]:
                      user?.role === userRoleType.privilegeOps ||
                      user?.role === userRoleType.generalOps ||
                      (isAdmin && user?.role === allUserRoles.admin),
                  })}
                  onClick={() => updateUserHandler(user)}
                >
                  Update
                </button>
              </Tooltip>
            </AccessControl>

            {!(isAdmin && user?.role === allUserRoles.admin) && (
              <AccessControl code="remove_user" page="organization-profile">
                <Tooltip placement="top" title={"Remove User"} arrow>
                  <div className={styles.item} onClick={() => setShowRemoveUserModal(user)}>
                    <SVG className={styles.actionIcon} iconName={"images/remove.svg"} />
                  </div>
                </Tooltip>
              </AccessControl>
            )}
          </div>
        </td>
      </tr>
    );
  });
};

const WorkSpaceMembers = ({
  getWorkspaceUsers,
  workspaceId,
  updateWorkspaceMember,
  removeWorkspaceMember,
  getWorkspaceDetails,
  workspaceDetails,
  entityConfig,
}) => {
  const { currentRole, selectedEntity } = useOutletContext();
  const isSuperAdmin = currentRole === allUserRoles.superAdmin;
  const isAdmin = currentRole === allUserRoles.admin;

  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(null);
  const [search, setSearch] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(null);
  const [orgWsData, setOrgWsData] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState([]);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);

  const updateUserRole = (userId, role) => {
    setUsers((prev) =>
      prev.map((user) => {
        if (user.userId === userId) {
          return { ...user, role };
        }

        return user;
      }),
    );
  };

  const updateUserHandler = useCallback(
    async (user) => {
      try {
        const payload = {
          workspaceMemberId: user?.workspaceMemberId,
          role: user?.role,
        };

        const response = await updateWorkspaceMember(payload);

        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
        if (error.message) {
          notification.error({ message: error.message });
        }
      } finally {
      }
    },
    [updateWorkspaceMember],
  );

  const fetchWorkspaceUsers = useCallback(
    async ({ page = 1, search = "" }) => {
      try {
        setLoading(true);

        const payload = {
          workspaceId,
          pageSize,
          page,
          search,
        };

        if (!isSuperAdmin) {
          payload.roleType = userRoleType.client;
        }
        const response = await getWorkspaceUsers(payload);

        if (response?.value?.status) {
          setUsers(response?.value?.users);
          setTotal(response?.value?.total);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [getWorkspaceUsers, isSuperAdmin, workspaceId],
  );

  const handleRemoveUser = useCallback(
    async (workspaceMemberId, search) => {
      try {
        setLoading(true);
        const payload = {
          workspaceMemberId: workspaceMemberId,
        };

        const response = await removeWorkspaceMember(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setShowRemoveUserModal(null);
        fetchWorkspaceUsers({ page: 1, search });
        setActivePage(1);
      }
    },
    [activePage, fetchWorkspaceUsers, removeWorkspaceMember],
  );

  const debouncedFetchWorkspaces = useMemo(
    () =>
      debounce((data) => {
        // setActivePage(1);
        fetchWorkspaceUsers(data);
      }, 800),
    [fetchWorkspaceUsers],
  );

  const onSearch = (e) => {
    setSearch(e.target.value);
    debouncedFetchWorkspaces({ page: 1, search: e.target.value });
  };

  const closeAndFetchAddUserModal = useCallback(() => {
    setShowAddUserModal(null);
    fetchWorkspaceUsers({ page: activePage, search });
  }, [activePage, fetchWorkspaceUsers, search]);

  const closeAndFetchAssignUserModal = useCallback(() => {
    setShowAssignUserModal(null);
    fetchWorkspaceUsers({ page: activePage, search });
  }, [activePage, fetchWorkspaceUsers, search]);

  const getEntityDetails = async () => {
    try {
      // first fetch workspace details
      const { value } = await getWorkspaceDetails({
        entityId: workspaceId,
        getConfig: false,
      });

      if (value?.status) {
        // workspace details fetched
        setOrgWsData(value?.workspace);
      }
      // if workspace, get its parent organization details
    } catch (error) {
      console.log("Error occured");
    }
  };
  useEffect(() => {
    getEntityDetails();
  }, [workspaceId]);
  return (
    <>
      <div className={styles.container}>
        <div className="title-block--op">
          <div>
            <h4>Users</h4>
          </div>
          <div className={classNames("right-filter-search--op", styles.rightContainer)}>
            <div className={styles.searchContainer}>
              <div className={styles.leftCont}>
                <SVG className={styles.filterIcon} iconName={"images/search.svg"} />
                <input
                  type="search"
                  name="org-search"
                  id="org-search"
                  placeholder="Search"
                  autoFocus
                  value={search}
                  onChange={onSearch}
                  className={styles.search}
                />
              </div>
              <FilterPopover
                dateRange={filterDateRange}
                setDateRange={setFilterDateRange}
                setIsModalOpen={setFilterPopupOpen}
                fetch={fetchWorkspaceUsers}
                isUserComponent={true}
                activePage={activePage}
                setActivePage={setActivePage}
                isModalOpen={filterPopupOpen}
              />
            </div>
            <div className={styles.ctaContainer}>
              <AccessControl code="add_user" page="organization-profile">
                <button
                  className={styles.addUserCta}
                  onClick={() =>
                    entityConfig?.maxNumberOfUsers > total
                      ? setShowAddUserModal(selectedEntity || workspaceDetails)
                      : notification.warning({
                          message: `You can add only ${entityConfig?.maxNumberOfUsers ? entityConfig?.maxNumberOfUsers : 3} Users.`,
                        })
                  }
                >
                  Add new user
                </button>
              </AccessControl>
              <AccessControl code="assign_user" page="organization-profile">
                <button
                  className={styles.assignUserCta}
                  onClick={() =>
                    entityConfig?.maxNumberOfUsers > total
                      ? setShowAssignUserModal(selectedEntity || workspaceDetails)
                      : notification.warning({
                          message: `You can add only ${entityConfig?.maxNumberOfUsers ? entityConfig?.maxNumberOfUsers : 3} Users.`,
                        })
                  }
                >
                  Assign user
                </button>
              </AccessControl>
            </div>
          </div>
        </div>
        {loading ? (
          <div className={styles.emptyContainer}>
            <LoadingOutlined style={{ fontSize: 30, align: "center", display: "inline-flex" }} spin />
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyContainer}>
            <Empty />
          </div>
        ) : (
          <div className="table-resposive table-container user-management-table normal-table-block">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>created AT</th>
                  <th>Email ID</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <Rows
                  users={users}
                  updateUserRole={updateUserRole}
                  updateUserHandler={updateUserHandler}
                  setShowRemoveUserModal={setShowRemoveUserModal}
                  isAdmin={isAdmin}
                  isSuperAdmin={isSuperAdmin}
                />
              </tbody>

              <tfoot></tfoot>
            </table>
          </div>
        )}
      </div>

      {users.length > 0 && (
        <Pagination
          showSizeChanger={false}
          className={styles.pagination}
          current={activePage}
          pageSize={pageSize}
          onChange={(activePage) => setActivePage(activePage)}
          total={total}
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          showModal={true}
          entity={showAddUserModal}
          entityType={ENTITY_TYPE.WORKSPACES}
          closeAndFetchModal={closeAndFetchAddUserModal}
          setShowModal={setShowAddUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showAssignUserModal && (
        <AssignUserModal
          entity={showAssignUserModal}
          entityType={ENTITY_TYPE.WORKSPACES}
          closeAndFetchModal={closeAndFetchAssignUserModal}
          setShowModal={setShowAssignUserModal}
          isAdmin={isAdmin}
        />
      )}

      {showRemoveUserModal && (
        <DeleteModal
          id={showRemoveUserModal?.workspaceMemberId}
          setId={setShowRemoveUserModal}
          handleDelete={() => handleRemoveUser(showRemoveUserModal?.workspaceMemberId, search)}
          content={`Are you sure you want to remove the user - ${showRemoveUserModal?.name} from the workspace ?`}
        />
      )}
    </>
  );
};

export default WorkSpaceMembers;
